import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Web as WebIcon,
  Comment as CommentIcon,
  Help as HelpIcon,
  FormatQuote as QuoteIcon,
  Category as CategoryIcon,
  Person as AuthorIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSPage, CMSBlogPost } from '../../services';

interface CMSStats {
  pages: number;
  blogPosts: number;
  blogPostsPublished: number;
  testimonials: number;
  faq: number;
  categories: number;
  authors: number;
}

const CMSDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<CMSStats>({
    pages: 0,
    blogPosts: 0,
    blogPostsPublished: 0,
    testimonials: 0,
    faq: 0,
    categories: 0,
    authors: 0,
  });
  const [recentPosts, setRecentPosts] = useState<CMSBlogPost[]>([]);
  const [pages, setPages] = useState<CMSPage[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all data in parallel
        const [pagesRes, postsRes, testimonialsRes, faqRes, categoriesRes, authorsRes] = await Promise.all([
          cmsService.getPages(),
          cmsService.getBlogPosts({ limit: 5 }),
          cmsService.getTestimonials(),
          cmsService.getFAQ(),
          cmsService.getBlogCategories(),
          cmsService.getBlogAuthors(),
        ]);

        setPages(pagesRes.data.data || []);
        setRecentPosts(postsRes.data.data || []);

        const allPosts = postsRes.data.data || [];
        const publishedPosts = allPosts.filter((p: CMSBlogPost) => p.status === 'publicado');

        setStats({
          pages: pagesRes.data.data?.length || 0,
          blogPosts: postsRes.data.pagination?.total || allPosts.length,
          blogPostsPublished: publishedPosts.length,
          testimonials: testimonialsRes.data.data?.length || 0,
          faq: faqRes.data.data?.length || 0,
          categories: categoriesRes.data.data?.length || 0,
          authors: authorsRes.data.data?.length || 0,
        });
      } catch (err: any) {
        console.error('Error fetching CMS data:', err);
        setError(err.message || t('cms.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ title, value, icon, color, onClick }: { title: string; value: number; icon: React.ReactNode; color: string; onClick?: () => void }) => (
    <Card sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 4 } : {} }} onClick={onClick}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15`, color, fontSize: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/cms/blog/posts/new')}
            sx={{ mr: 1 }}
          >
            {t('cms.newArticle')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/cms/settings')}
          >
            {t('sidebar.settings')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('cms.pages')}
            value={stats.pages}
            icon={<WebIcon />}
            color="#1976d2"
            onClick={() => navigate('/cms/pages')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('cms.blogPosts')}
            value={stats.blogPosts}
            icon={<ArticleIcon />}
            color="#9c27b0"
            onClick={() => navigate('/cms/blog/posts')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t('cms.testimonials')}
            value={stats.testimonials}
            icon={<QuoteIcon />}
            color="#2e7d32"
            onClick={() => navigate('/cms/testimonials')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="FAQ"
            value={stats.faq}
            icon={<HelpIcon />}
            color="#ed6c02"
            onClick={() => navigate('/cms/faq')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Blog Posts */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  {t('cms.recentArticles')}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/cms/blog/posts')}
                >
                  {t('cms.viewAll')}
                </Button>
              </Box>
              <List>
                {recentPosts.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary={t('cms.noArticles')}
                      secondary={t('cms.createFirstArticle')}
                    />
                  </ListItem>
                ) : (
                  recentPosts.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <ListItem>
                        <ListItemIcon>
                          <ArticleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={post.titulo}
                          secondary={
                            <Box component="span" display="flex" alignItems="center" gap={1}>
                              <Chip
                                size="small"
                                label={post.status === 'publicado' ? t('cms.published') : t('cms.draft')}
                                color={post.status === 'publicado' ? 'success' : 'default'}
                              />
                              {post.categoria_nome && (
                                <Chip size="small" label={post.categoria_nome} variant="outlined" />
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/cms/blog/posts/${post.id}`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < recentPosts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Links & Pages */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {t('cms.landingPages')}
              </Typography>
              <List dense>
                {pages.slice(0, 6).map((page) => (
                  <ListItem key={page.id} disablePadding secondaryAction={
                    <Chip
                      size="small"
                      label={page.status}
                      color={page.status === 'publicado' ? 'success' : 'default'}
                    />
                  }>
                    <ListItemButton onClick={() => navigate(`/cms/pages/${page.id}`)}>
                      <ListItemIcon>
                        <WebIcon color={page.status === 'publicado' ? 'primary' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={page.titulo}
                        secondary={`/${page.slug}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Button
                fullWidth
                variant="text"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/cms/pages')}
                sx={{ mt: 1 }}
              >
                {t('cms.viewAllPages')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                {t('cms.quickActions')}
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ArticleIcon />}
                    onClick={() => navigate('/cms/blog/posts/new')}
                  >
                    {t('cms.newArticle')}
                  </Button>
                </Grid>
                <Grid size={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<QuoteIcon />}
                    onClick={() => navigate('/cms/testimonials')}
                  >
                    {t('cms.testimonials')}
                  </Button>
                </Grid>
                <Grid size={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<HelpIcon />}
                    onClick={() => navigate('/cms/faq')}
                  >
                    {t('cms.newFaq')}
                  </Button>
                </Grid>
                <Grid size={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CategoryIcon />}
                    onClick={() => navigate('/cms/blog/categories')}
                  >
                    {t('sidebar.categories')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CMSDashboard;
